import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowRight, Flame, Target, Zap, Brain, MapPin, CheckCircle2, Lock, ChevronRight } from "lucide-react";
import Marquee from "@/components/Marquee";
import { Reveal, MaskedLine, SectionTag } from "@/components/Reveal";

const IMG = {
  coach_hero: "https://customer-assets-lqy194kg.emergentagent.net/job_hoops-iq-academy/artifacts/d2dc2680473b8323_theo.JPG.webp",
  hero: "https://customer-assets-lqy194kg.emergentagent.net/job_hoops-iq-academy/artifacts/0bd63bsj_unnamed%20%283%29.jpg",
  theo: "https://customer-assets-lqy194kg.emergentagent.net/job_hoops-iq-academy/artifacts/4msa7t11_unnamed%20%282%29.jpg",
  defense: "https://customer-assets-lqy194kg.emergentagent.net/job_hoops-iq-academy/artifacts/vci18148_unnamed%20%284%29.jpg",
  albany: "https://customer-assets-lqy194kg.emergentagent.net/job_hoops-iq-academy/artifacts/omqvg6bj_unnamed%20%285%29.jpg",
  encinal: "https://customer-assets-lqy194kg.emergentagent.net/job_hoops-iq-academy/artifacts/ygac19br_Screenshot%202026-08-24%20at%2012.26.55%E2%80%AFPM.png",
  coach: "https://customer-assets-lqy194kg.emergentagent.net/job_hoops-iq-academy/artifacts/n18k3yze_Screenshot%202026-08-24%20at%2012.25.42%E2%80%AFPM.png",
  family: "https://customer-assets-lqy194kg.emergentagent.net/job_hoops-iq-academy/artifacts/gdpswq6y_Screenshot%202026-08-24%20at%2012.27.33%E2%80%AFPM.png",
  sanleandro: "https://customer-assets-lqy194kg.emergentagent.net/job_hoops-iq-academy/artifacts/u2zyrt60_IMG_7499.webp",
  group: "https://images.pexels.com/photos/5785714/pexels-photo-5785714.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  huddle: "https://images.unsplash.com/photo-1521830101529-057b1dfd9784?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHwzfHxjb2FjaCUyMHRhbGtpbmclMjB0byUyMGJhc2tldGJhbGwlMjB0ZWFtJTIwdGltZW91dHxlbnwwfHx8fDE3ODcyNTI1MzJ8MA&ixlib=rb-4.1.0&q=85",
  shooting: "https://images.unsplash.com/photo-1546519638-68e109498ffc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1Mjh8MHwxfHNlYXJjaHwxfHxiYXNrZXRiYWxsJTIwcGxheWVyJTIwc2hvb3RpbmclMjBjb3VydCUyMHNsYW0lMjBkdW5rfGVufDB8fHx8MTc4NzI1MjUyNXww&ixlib=rb-4.1.0&q=85",
  scrimmage: "https://images.pexels.com/photos/974509/pexels-photo-974509.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  hardwood: "https://images.unsplash.com/photo-1703177392732-65d365de430d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHw0fHxiYXNrZXRiYWxsJTIwdHJhaW5pbmclMjBhdGhsZXRlJTIwZ3ltJTIwZHJpbGx8ZW58MHx8fHwxNzg3MjUyNTI0fDA&ixlib=rb-4.1.0&q=85",
};

const CHAPTERS = [
  { n: "01", icon: Flame, title: "Private Gym Immersion", img: IMG.theo,
    text: "No crowded rec centers. No waiting for a rim. Every session runs in a private Alameda gym where your athlete gets every rep, every correction, and Theo's full attention." },
  { n: "02", icon: Target, title: "D1 Coach Network", img: IMG.huddle,
    text: "Theo keeps direct lines to college coaches and scouts. When your athlete is ready, the right eyes already know their name — referrals, film exposure, and honest recruiting guidance." },
  { n: "03", icon: Brain, title: "Biomechanics & IQ", img: IMG.defense,
    text: "Shot mechanics rebuilt rep by rep. Deceleration and agility trained like a science. Live-read decision making drilled until the game slows down for them." },
];

const TESTIMONIALS = [
  { quote: "Your workouts were different because we got to work on moves that we can actually translate to a basketball game — some moves from other people don't make it to a basketball game for me personally.", name: "Chris Lee", role: "Encinal Varsity" },
  { quote: "Your workouts aren't just a bunch of dribbles into a shot — it's more game-like situations that you put me in, so I'm familiar with it once I'm actually playing against competition. And even with a bigger group of athletes, your workouts are still interpersonal. No one feels left out or confused.", name: "LeBrie Goudy-Lee", role: "Bluefield State" },
  { quote: "Coach Theo's personalized training has made a noticeable difference in my son's strength, confidence, and overall performance. His tailored workouts, combined with positive support and encouragement, have truly helped my son improve and reach his potential.", name: "Lisa C.", role: "Parent" },
];

const GALLERY = [
  { img: IMG.hero, label: "Game-night elevation" },
  { img: IMG.theo, label: "Running the lab" },
  { img: IMG.defense, label: "Lockdown stance" },
  { img: IMG.albany, label: "Albany Cougars #12" },
  { img: IMG.encinal, label: "Chris Lee • Encinal Varsity" },
  { img: IMG.family, label: "Pure game-day passion" },
  { img: "/images/sanleandro-game.jpg", label: "Game action • San Leandro" },
];

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 110]);

  return (
    <main>
      {/* HERO */}
      <section ref={heroRef} data-testid="hero-section" className="relative min-h-screen court-grid-bg flex items-center pt-16 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-court-amber/10 via-transparent to-court-void pointer-events-none" />
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20 grid lg:grid-cols-2 gap-14 items-center relative z-10">
          <div>
            <motion.div
              initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
              data-testid="hero-badge"
              className="flex flex-wrap items-center gap-2 border border-court-amber/40 bg-court-amber/10 rounded-full px-4 py-1.5 mb-8 max-w-full"
            >
              <MapPin size={13} className="text-court-amber shrink-0" />
              <span className="font-mono text-[10px] sm:text-xs uppercase tracking-widest text-court-amber font-semibold">Alameda, CA • Private Gym Facility • Ages 11 to D1</span>
            </motion.div>
            <h1 data-testid="hero-headline" className="font-display font-black uppercase leading-[0.9] tracking-tighter text-4xl sm:text-5xl lg:text-6xl">
              <MaskedLine delay={0.15}>Sharper handles.</MaskedLine>
              <MaskedLine delay={0.3}>Purer shot.</MaskedLine>
              <MaskedLine delay={0.45}><span className="text-court-amber">Real D1 IQ.</span></MaskedLine>
            </h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.75, duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
              className="mt-6 text-base md:text-lg text-zinc-400 max-w-lg leading-relaxed"
            >
              Elite 1-on-1 and small-group basketball training for middle school through Division 1 athletes — boys and girls — built inside Alameda's private gyms by Coach Theo.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
              className="mt-9 flex flex-wrap gap-4"
            >
              <Link data-testid="hero-book-cta" to="/schedule" className="btn-amber bg-court-amber text-court-void font-display font-extrabold uppercase tracking-wide text-lg px-8 py-3.5 rounded-lg flex items-center gap-2">
                Book a Session <ArrowRight size={18} />
              </Link>
              <Link data-testid="hero-about-cta" to="/about" className="border border-white/20 hover:border-court-amber/60 text-zinc-200 font-display font-bold uppercase tracking-wide text-lg px-8 py-3.5 rounded-lg transition-colors">
                Meet Coach Theo
              </Link>
            </motion.div>
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.1, duration: 0.8 }}
              className="mt-12 flex gap-10"
            >
              {[["$60", "Private 1-on-1"], ["$30", "Group Drop-in"], ["D1", "Coach Network"]].map(([big, small]) => (
                <div key={small}>
                  <div className="font-display font-black text-3xl text-court-amber">{big}</div>
                  <div className="font-mono text-xs uppercase tracking-widest text-zinc-500 mt-1">{small}</div>
                </div>
              ))}
            </motion.div>
          </div>

          <motion.div style={{ y: imgY }} className="relative mt-2 lg:mt-0">
            <motion.div
              initial={{ opacity: 0, scale: 0.94 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.4, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
              className="spotlight-frame relative overflow-hidden border border-white/10"
            >
              <div className="absolute inset-0 bg-court-amber/15 mix-blend-overlay z-10 pointer-events-none" />
              <img src={IMG.coach_hero} alt="Coach Theo directing a session in the gym" className="w-full h-[300px] sm:h-[420px] lg:h-[560px] object-cover object-top" data-testid="hero-image" />
              <div className="absolute inset-0 bg-gradient-to-t from-court-void/80 via-transparent to-transparent z-10" />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 1.0, duration: 0.6 }}
              className="absolute -bottom-5 -left-6 bg-court-surface/95 backdrop-blur-md border border-court-amber/30 rounded-xl px-5 py-4 shadow-2xl z-20"
            >
              <div className="font-mono text-xs uppercase tracking-widest text-court-amber font-bold">Now Booking</div>
              <div className="text-sm text-zinc-300 mt-1">Weekday evenings + Saturday mornings</div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      <Marquee />

      {/* MANIFESTO CHAPTERS */}
      <section data-testid="manifesto-section" className="max-w-7xl mx-auto px-5 sm:px-8 py-28">
        <Reveal>
          <SectionTag index="CH.01–03" label="The Lab Manifesto" />
          <h2 className="font-display font-extrabold uppercase tracking-tight leading-[0.95] text-2xl sm:text-3xl lg:text-4xl max-w-2xl">
            Built different, <span className="text-court-amber">on purpose.</span>
          </h2>
        </Reveal>
        <div className="mt-14 space-y-6">
          {CHAPTERS.map((c, i) => (
            <Reveal key={c.n} delay={i * 0.08}>
              <div className={`card-lift group grid md:grid-cols-12 gap-0 bg-court-surface/80 border border-white/10 rounded-2xl overflow-hidden ${i % 2 === 1 ? "md:[direction:rtl]" : ""}`}>
                <div className="md:col-span-4 relative h-56 md:h-auto overflow-hidden [direction:ltr]">
                  <img src={c.img} alt={c.title} className="gallery-img absolute inset-0 w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent to-court-surface/70" />
                </div>
                <div className="md:col-span-8 p-8 md:p-10 [direction:ltr]">
                  <div className="flex items-baseline gap-4">
                    <span className="font-display font-black text-5xl text-outline">{c.n}</span>
                    <c.icon size={22} className="text-court-amber" />
                  </div>
                  <h3 className="font-display font-bold uppercase tracking-tight text-xl sm:text-2xl mt-3">{c.title}</h3>
                  <p className="text-sm sm:text-base text-zinc-400 leading-relaxed mt-3 max-w-2xl">{c.text}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* PRICING */}
      <section data-testid="pricing-section" className="relative py-28 border-y border-white/10 bg-court-surface/40 court-grid-bg">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Reveal>
            <SectionTag index="SEC.02" label="Programs & Pricing" />
            <h2 className="font-display font-extrabold uppercase tracking-tight leading-[0.95] text-2xl sm:text-3xl lg:text-4xl">
              Two ways in. <span className="text-court-amber">One standard.</span>
            </h2>
            <p className="mt-4 text-sm sm:text-base text-zinc-400 max-w-xl">No online payment required — pay in person at the gym with cash, Zelle, or Venmo.</p>
          </Reveal>
          <div className="mt-14 grid md:grid-cols-2 gap-6 max-w-4xl">
            <Reveal delay={0.05}>
              <div data-testid="pricing-private-card" className="card-lift relative bg-court-surface/90 backdrop-blur-md border border-court-amber/40 rounded-2xl p-8 h-full">
                <span className="absolute -top-3 left-6 bg-court-amber text-court-void font-mono text-xs font-bold uppercase tracking-widest px-3 py-1 rounded-full">Most Popular for Recruits</span>
                <div className="font-mono text-xs uppercase tracking-widest text-zinc-500">Private 1-on-1</div>
                <div className="flex items-baseline gap-2 mt-3">
                  <span className="font-display font-black text-6xl text-court-amber">$60</span>
                  <span className="text-sm text-zinc-500">/ 60-min session</span>
                </div>
                <ul className="mt-6 space-y-3">
                  {["Private 1-on-1 floor access", "Custom video breakdown & shot mechanics overhaul", "Direct progress reporting to parents", "D1 collegiate scout & coach network referrals"].map((f) => (
                    <li key={f} className="flex gap-3 text-sm text-zinc-300"><CheckCircle2 size={16} className="text-court-amber shrink-0 mt-0.5" />{f}</li>
                  ))}
                </ul>
                <Link data-testid="pricing-private-cta" to="/schedule" className="btn-amber mt-8 block text-center bg-court-amber text-court-void font-display font-extrabold uppercase tracking-wide text-lg py-3 rounded-lg">
                  Request 1-on-1 Session
                </Link>
              </div>
            </Reveal>
            <Reveal delay={0.15}>
              <div data-testid="pricing-group-card" className="card-lift bg-court-surface/90 backdrop-blur-md border border-white/10 rounded-2xl p-8 h-full">
                <div className="font-mono text-xs uppercase tracking-widest text-zinc-500">Group Drop-in</div>
                <div className="flex items-baseline gap-2 mt-3">
                  <span className="font-display font-black text-6xl">$30</span>
                  <span className="text-sm text-zinc-500">/ drop-in session</span>
                </div>
                <ul className="mt-6 space-y-3">
                  {["Competitive 4-8 player high-intensity lab", "Game-speed decision making & live reads", "Agility, footwork & defensive sliding ladders", "Weekly weekend & weekday evening slots"].map((f) => (
                    <li key={f} className="flex gap-3 text-sm text-zinc-300"><CheckCircle2 size={16} className="text-court-amber shrink-0 mt-0.5" />{f}</li>
                  ))}
                </ul>
                <Link data-testid="pricing-group-cta" to="/schedule" className="mt-8 block text-center border border-white/20 hover:border-court-amber/60 text-zinc-100 font-display font-bold uppercase tracking-wide text-lg py-3 rounded-lg transition-colors">
                  Join Group Drop-in
                </Link>
              </div>
            </Reveal>
          </div>
          <Reveal delay={0.2}>
            <div className="mt-8 max-w-4xl flex flex-wrap items-center justify-between gap-4 bg-court-surface/80 border border-court-amber/25 rounded-2xl px-7 py-6">
              <p className="text-sm text-zinc-300"><span className="text-court-amber font-bold">New: monthly training packages.</span> Bundles that save up to 20%+ versus single sessions — College Recruit, Starting 5 Varsity, JV Jumpstart and more.</p>
              <Link data-testid="pricing-packages-link" to="/packages" className="font-mono text-xs uppercase tracking-widest text-court-amber hover:text-court-amberhover transition-colors flex items-center gap-1.5 shrink-0">View Packages <ArrowRight size={12} /></Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* SUMMER CAMP BANNER */}
      <section data-testid="camp-banner" className="relative overflow-hidden">
        <img src={IMG.huddle} alt="Courtside huddle" className="absolute inset-0 w-full h-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-r from-court-void via-court-void/85 to-court-void/40" />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 py-28">
          <Reveal>
            <SectionTag index="NEW" label="Summer 2027 Expansion" />
            <h2 className="font-display font-black uppercase tracking-tighter leading-[0.9] text-4xl sm:text-5xl lg:text-6xl max-w-3xl">
              Pre-season <span className="text-court-amber">elite camp</span> is coming.
            </h2>
            <p className="mt-5 text-base md:text-lg text-zinc-400 max-w-xl leading-relaxed">
              Week-long camp-style training in June & July 2027 to get your athlete fall-season ready: handles, shooting volume, agility testing, and IQ film sessions.
            </p>
            <Link data-testid="camp-banner-cta" to="/summer-camp" className="btn-amber mt-8 inline-flex items-center gap-2 bg-court-amber text-court-void font-display font-extrabold uppercase tracking-wide text-lg px-8 py-3.5 rounded-lg">
              See Camp Dates & Pricing <ChevronRight size={18} />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* PLANS TEASER */}
      <section data-testid="plans-teaser" className="max-w-7xl mx-auto px-5 sm:px-8 py-28">
        <Reveal>
          <SectionTag index="SEC.04" label="Workout & Training Plans" />
          <h2 className="font-display font-extrabold uppercase tracking-tight leading-[0.95] text-2xl sm:text-3xl lg:text-4xl">
              The blueprint <span className="text-court-amber">vault.</span>
          </h2>
          <p className="mt-4 text-sm sm:text-base text-zinc-400 max-w-xl">Free previews for everyone. Full pro blueprints unlock once Theo confirms your in-person payment.</p>
        </Reveal>
        <div className="mt-12 grid sm:grid-cols-3 gap-6">
          {[
            { title: "Shooting Warmup Routine", tag: "Free", locked: false, img: IMG.shooting },
            { title: "Footwork Pyramid", tag: "Free", locked: false, img: IMG.scrimmage },
            { title: "4-Week Sniper Shot Prep", tag: "Members", locked: true, img: IMG.hero },
          ].map((p, i) => (
            <Reveal key={p.title} delay={i * 0.08}>
              <Link to="/plans" data-testid={`plan-teaser-${i}`} className="card-lift group block bg-court-surface/80 border border-white/10 rounded-2xl overflow-hidden">
                <div className="relative h-44 overflow-hidden">
                  <img src={p.img} alt={p.title} className="gallery-img w-full h-full object-cover" />
                  <span className={`absolute top-3 right-3 font-mono text-xs font-bold uppercase tracking-widest px-3 py-1 rounded-full flex items-center gap-1.5 ${p.locked ? "bg-court-void/90 text-court-amber border border-court-amber/40" : "bg-court-amber text-court-void"}`}>
                    {p.locked && <Lock size={11} />}{p.tag}
                  </span>
                </div>
                <div className="p-5">
                  <h3 className="font-display font-bold uppercase tracking-tight text-xl sm:text-2xl">{p.title}</h3>
                  <span className="mt-2 inline-flex items-center gap-1 font-mono text-xs uppercase tracking-widest text-court-amber">Open plan <ArrowRight size={12} /></span>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section data-testid="testimonials-section" className="border-y border-white/10 bg-court-surface/40 py-28">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Reveal>
            <SectionTag index="SEC.05" label="Parents & Players" />
            <h2 className="font-display font-extrabold uppercase tracking-tight leading-[0.95] text-2xl sm:text-3xl lg:text-4xl">
              Proof from <span className="text-court-amber">the hardwood.</span>
            </h2>
          </Reveal>
          <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {TESTIMONIALS.map((t, i) => (
              <Reveal key={t.name} delay={i * 0.06}>
                <figure data-testid={`testimonial-${i}`} className="card-lift bg-court-surface/90 border border-white/10 rounded-2xl p-8 h-full flex flex-col">
                  <span className="font-display font-black text-5xl text-court-amber/40 leading-none">"</span>
                  <blockquote className="text-sm sm:text-base text-zinc-300 leading-relaxed mt-2 flex-1">{t.quote}</blockquote>
                  <figcaption className="mt-6">
                    <div className="font-display font-bold uppercase tracking-wide text-lg">{t.name}</div>
                    <div className="font-mono text-xs uppercase tracking-widest text-zinc-500">{t.role}</div>
                  </figcaption>
                </figure>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* GALLERY */}
      <section data-testid="gallery-section" className="max-w-7xl mx-auto px-5 sm:px-8 py-28">
        <Reveal>
          <SectionTag index="SEC.06" label="Inside The Lab" />
          <h2 className="font-display font-extrabold uppercase tracking-tight leading-[0.95] text-2xl sm:text-3xl lg:text-4xl">
              Midnight gym <span className="text-court-amber">energy.</span>
          </h2>
        </Reveal>
        <div className="mt-12 grid grid-cols-2 md:grid-cols-3 gap-4">
          {GALLERY.map((g, i) => (
            <Reveal key={g.label} delay={i * 0.05}>
              <div data-testid={`gallery-item-${i}`} className="group relative overflow-hidden rounded-xl border border-white/10 aspect-[4/3]">
                <img src={g.img} alt={g.label} className="gallery-img w-full h-full object-cover object-top" />
                <div className="absolute inset-0 bg-gradient-to-t from-court-void/90 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <span className="absolute bottom-3 left-3 font-mono text-xs uppercase tracking-widest text-court-amber opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all duration-500">{g.label}</span>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* FINAL CTA */}
      <section data-testid="final-cta" className="relative border-t border-white/10 court-grid-bg">
        <div className="absolute inset-0 bg-gradient-to-b from-court-amber/10 to-transparent pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 py-28 text-left">
          <Reveal>
            <h2 className="font-display font-black uppercase tracking-tighter leading-[0.9] text-4xl sm:text-5xl lg:text-6xl">
              Ready to <span className="text-court-amber">put in work?</span>
            </h2>
            <p className="mt-5 text-base md:text-lg text-zinc-400 max-w-xl">Check Theo's live availability and send a booking request in under a minute.</p>
            <div className="mt-9 flex flex-wrap gap-4">
              <Link data-testid="final-book-cta" to="/schedule" className="btn-amber bg-court-amber text-court-void font-display font-extrabold uppercase tracking-wide text-lg px-8 py-3.5 rounded-lg flex items-center gap-2">
                View Schedule <Zap size={18} />
              </Link>
              <Link data-testid="final-plans-cta" to="/plans" className="border border-white/20 hover:border-court-amber/60 text-zinc-200 font-display font-bold uppercase tracking-wide text-lg px-8 py-3.5 rounded-lg transition-colors">
                Browse Training Plans
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
